import { guestApi, type GuestRoomSummary } from '../api/guest';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { enhanceDataTableCards } from '../ui/dataTableCards';
import { createHelpTip } from '../help/helpTips';
import { formatSedrDateTime, tSedrLifecycle } from './sedr/sedrUi';
import { GuestAccountSedrPage } from './GuestAccountSedrPage';
import {
  parseGuestSedrRoomUrl,
  type GuestSedrRoomUrlState,
} from '../app/navigationState';

function prefersSedrMotion(): boolean {
  return (
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    && !window.matchMedia('(max-width: 768px)').matches
  );
}

function getGuestRoomUrlState(): GuestSedrRoomUrlState | null {
  return parseGuestSedrRoomUrl();
}

function getGuestRoomId(): string | null {
  return getGuestRoomUrlState()?.roomId ?? null;
}

function tGuestRole(role: string): string {
  const key = `sedr.access.permission.${role}`;
  const translated = t(key);
  return translated === key ? role : translated;
}

type GuestListHandle = {
  element: HTMLElement;
  reload: () => Promise<void>;
};

function GuestRoomListPage(): GuestListHandle {
  const page = document.createElement('section');
  page.className = 'module-page module-page--sedr module-page--guest-portal';
  page.innerHTML = `
    <div class="module-section">
      <div class="module-section__header">
        <div class="module-section__header-main">
          <h2 data-i18n="guest.home.title"></h2>
          <p class="muted" data-i18n="guest.home.subtitle"></p>
        </div>
      </div>
      <div id="guest-room-list" class="files-table-wrap"></div>
      <p class="error-text" id="guest-home-error" hidden></p>
    </div>
  `;
  applyI18n(page);
  page.querySelector('.module-section__header-main h2')?.append(createHelpTip('guest-sedr-dashboard'));

  const listEl = page.querySelector('#guest-room-list') as HTMLElement;
  const errorEl = page.querySelector('#guest-home-error') as HTMLParagraphElement;

  const renderList = (rooms: GuestRoomSummary[]) => {
    if (rooms.length === 0) {
      listEl.innerHTML = `<p class="muted" data-i18n="guest.home.empty"></p>`;
      applyI18n(listEl);
      return;
    }

    listEl.innerHTML = `
      <table class="data-table">
        <thead>
          <tr>
            <th data-i18n="sedr.list.colTitle"></th>
            <th data-i18n="sedr.list.colClient"></th>
            <th data-i18n="guest.home.colRole"></th>
            <th data-i18n="sedr.list.colStatus"></th>
            <th data-i18n="sedr.list.colLastModified"></th>
            <th class="data-table__actions-heading" data-i18n="sedr.list.colActions"></th>
          </tr>
        </thead>
        <tbody></tbody>
      </table>
    `;
    applyI18n(listEl);
    const tbody = listEl.querySelector('tbody') as HTMLTableSectionElement;
    const activeRoomId = getGuestRoomId();
    for (const room of rooms) {
      const tr = document.createElement('tr');
      if (activeRoomId === room.roomId) {
        tr.classList.add('is-active');
      }
      const link = document.createElement('a');
      link.href = `/guest/rooms/${encodeURIComponent(room.roomId)}`;
      link.textContent = room.title;
      tr.innerHTML = `
        <td></td>
        <td></td>
        <td></td>
        <td class="sedr-list-status"><div class="sedr-list-status__inner"><span class="status-badge"></span></div></td>
        <td class="sedr-list-modified"></td>
        <td class="data-table__actions"></td>
      `;
      tr.querySelector('td')?.append(link);
      (tr.children[1] as HTMLTableCellElement).textContent = room.clientOrganization || '—';
      (tr.children[2] as HTMLTableCellElement).textContent = tGuestRole(room.guestRole);
      const badge = tr.querySelector('.status-badge') as HTMLElement;
      badge.textContent = room.archived
        ? t('sedr.archive.status')
        : tSedrLifecycle(room.lifecycleState);
      if (room.archived) {
        badge.classList.add('status-badge--muted');
      } else if (
        room.lifecycleState === 'PendingDeletion'
        || room.lifecycleState === 'DeletionRequested'
      ) {
        badge.classList.add('status-badge--danger');
      }

      const modifiedCell = tr.querySelector('.sedr-list-modified') as HTMLTableCellElement;
      const modified = document.createElement('span');
      modified.textContent = formatSedrDateTime(room.lastModifiedAt);
      const lastViewed = room.lastViewedAt ? new Date(room.lastViewedAt).getTime() : 0;
      const lastModified = room.lastModifiedAt ? new Date(room.lastModifiedAt).getTime() : 0;
      if (!room.lastViewedAt || lastModified > lastViewed) {
        modified.className = 'sedr-list-modified--unread';
        modified.title = t('sedr.list.unreadHint');
      }
      modifiedCell.append(modified);

      const openBtn = document.createElement('a');
      openBtn.className = 'button subtle';
      openBtn.href = `/guest/rooms/${encodeURIComponent(room.roomId)}`;
      openBtn.setAttribute('data-i18n', 'guest.home.open');
      tr.querySelector('.data-table__actions')?.append(openBtn);
      applyI18n(tr);
      tbody.appendChild(tr);
    }
    const table = listEl.querySelector('table');
    if (table) enhanceDataTableCards(table);
  };

  const load = async () => {
    errorEl.hidden = true;
    try {
      const rooms = await guestApi.listRooms();
      renderList(rooms);
    } catch (cause) {
      console.error(cause);
      errorEl.hidden = false;
      errorEl.textContent = t('status.error');
    }
  };

  void load();
  bindLocaleRefresh(() => {
    applyI18n(page);
    void load();
  });

  return { element: page, reload: load };
}

type MountedPane =
  | { kind: 'none' }
  | { kind: 'room'; roomId: string };

export function GuestSedrModulePage(): HTMLElement {
  const page = document.createElement('div');
  page.className = 'sedr-module';

  const workspace = document.createElement('div');
  workspace.className = 'sedr-workspace sedr-browse-layout';
  workspace.setAttribute('data-guest-sedr-workspace', '');

  const gutter = document.createElement('div');
  gutter.className = 'sedr-workspace__gutter';
  gutter.setAttribute('aria-hidden', 'true');

  const listPane = document.createElement('div');
  listPane.className = 'sedr-workspace__list';
  const listHandle = GuestRoomListPage();
  listPane.append(listHandle.element);

  const roomPane = document.createElement('div');
  roomPane.className = 'sedr-workspace__room';

  workspace.append(gutter, listPane, roomPane);
  page.append(workspace);

  let mounted: MountedPane = { kind: 'none' };
  let lastDetailPath = window.location.pathname;
  let disposePane: (() => void) | null = null;
  let allowMotion = false;

  const afterCloseMotion = (done: () => void) => {
    const animate = allowMotion && prefersSedrMotion();
    if (!animate) {
      done();
      return;
    }
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      workspace.removeEventListener('transitionend', onTransitionEnd);
      done();
    };
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== workspace || event.propertyName !== 'grid-template-columns') return;
      finish();
    };
    workspace.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(finish, 1100);
  };

  const clearPane = () => {
    disposePane?.();
    disposePane = null;
    mounted = { kind: 'none' };
    roomPane.replaceChildren();
  };

  const syncRoute = () => {
    const urlState = getGuestRoomUrlState();
    const roomId = urlState?.roomId ?? null;
    const open = Boolean(roomId);
    const animate = allowMotion && prefersSedrMotion();
    const closing = mounted.kind !== 'none' && !open;

    workspace.classList.toggle('sedr-workspace--instant', !animate);
    workspace.classList.toggle('sedr-workspace--closing', animate && closing);
    workspace.classList.toggle('sedr-workspace--open', open);

    if (!animate) {
      workspace.classList.toggle('sedr-workspace--ready', open);
    }

    if (roomId && mounted.kind === 'room' && mounted.roomId === roomId && !closing) {
      const currentPath = window.location.pathname;
      if (currentPath !== lastDetailPath) {
        lastDetailPath = currentPath;
        roomPane.firstElementChild?.dispatchEvent(new Event('guest-sedr:room-nav'));
      }
      return;
    }

    lastDetailPath = window.location.pathname;

    if (!open) {
      const shouldReloadList = mounted.kind !== 'none';
      if (shouldReloadList) {
        void listHandle.reload();
      }
      workspace.classList.remove('sedr-workspace--ready');
      afterCloseMotion(() => {
        if (getGuestRoomId()) return;
        clearPane();
        workspace.classList.remove('sedr-workspace--closing');
      });
      return;
    }

    workspace.classList.remove('sedr-workspace--closing');

    if (mounted.kind !== 'room' || mounted.roomId !== roomId) {
      clearPane();
      const roomPage = GuestAccountSedrPage(roomId!);
      roomPane.append(roomPage);
      disposePane = () => {
        roomPane.replaceChildren();
      };
      mounted = { kind: 'room', roomId: roomId! };
      void listHandle.reload();
    }

    if (animate) {
      requestAnimationFrame(() => {
        workspace.classList.add('sedr-workspace--ready');
      });
    }
  };

  workspace.addEventListener('guest-sedr:route', () => syncRoute());
  syncRoute();

  requestAnimationFrame(() => {
    allowMotion = true;
  });

  const observer = new MutationObserver(() => {
    if (!page.isConnected) {
      disposePane?.();
      disposePane = null;
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return page;
}
